import React from 'react'
import { Box, Typography, Paper, useTheme } from '@mui/material'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import ReceiptIcon from '@mui/icons-material/Receipt'

export type PaymentMethod = 'credit_card' | 'invoice'

interface PaymentMethodSelectorProps {
  selectedMethod: PaymentMethod
  onMethodChange: (method: PaymentMethod) => void
  // Monthly billing is credit-card only — hide invoice when monthly is chosen.
  hideInvoice?: boolean
}

export default function PaymentMethodSelector({
  selectedMethod,
  onMethodChange,
  hideInvoice = false,
}: PaymentMethodSelectorProps) {
  const theme = useTheme()

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
        Choose Payment Method
      </Typography>

      {hideInvoice && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
          data-testid="monthly-credit-card-only-note"
        >
          Monthly billing is by credit card. Choose quarterly or annual billing
          to pay by invoice.
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: 'row',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
          },
        }}
      >
        {/* Credit Card Option */}
        <Paper
          elevation={selectedMethod === 'credit_card' ? 2 : 0}
          sx={{
            p: 3,
            border:
              selectedMethod === 'credit_card'
                ? `2px solid ${theme.palette.primary.main}`
                : '1px solid #e0e0e0',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: 1,
            '&:hover': {
              borderColor: theme.palette.primary.main,
            },
          }}
          onClick={() => onMethodChange('credit_card')}
        >
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CreditCardIcon color="primary" />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                }}
              >
                Credit Card
              </Typography>
            </Box>
            <>
              <Typography variant="body2" color="text.secondary">
                Pay with your credit card. Your account will be activated
                immediately after payment.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  • Automatic billing
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  • Secure payment processing
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  • Cancel anytime
                </Typography>
              </Box>
            </>
          </Box>
        </Paper>

        {/* Invoice Option — hidden for monthly billing */}
        {!hideInvoice && (
          <Paper
            elevation={selectedMethod === 'invoice' ? 2 : 0}
            sx={{
              p: 3,
              border:
                selectedMethod === 'invoice'
                  ? `2px solid ${theme.palette.primary.main}`
                  : '1px solid #e0e0e0',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              flex: 1,
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
            }}
            onClick={() => onMethodChange('invoice')}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReceiptIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Pay by Invoice
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                We'll send you an invoice via email. Your account will be
                activated when the invoice is sent.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  • 7-day payment terms
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  • Perfect for organisations
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  )
}
